import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetDocuementsResponse, Product } from '@interfaces/products.interface';
import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Grid,
  Group,
  NumberInput,
  Space,
  Text,
} from '@mantine/core';
import { closeModal, openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { IconDownload, IconEye, IconShoppingCart } from '@tabler/icons-react';

import { useGetCartQuery, useUpdateCartMutation } from '@api/cart.api';
import { isApiError } from '@api/index';
import { useLazyGetDocumentsQuery } from '@api/products.api';

import useAuth from '@hooks/useAuth';

import LoginForm from '@components/LoginForm';

import classes from './ProductModal.module.css';

interface ProductmodalProps {
  product: Product;
  category: string;
}

const normalizeUrl = (url: string) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `https://${url}`;
};

export const PRODUCT_MODAL_ID = 'productModal';

export default function ProductModal({ product, category }: ProductmodalProps) {
  const { t } = useTranslation();

  // ==========================================================================
  // General
  // ==========================================================================
  const { isAuthenticated } = useAuth();
  const [documents, setDocuments] = useState<GetDocuementsResponse[]>();

  // ==========================================================================
  // State
  // ==========================================================================
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [showAddedToCartMessage, setShowAddedToCartMessage] = useState(false);

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: cart } = useGetCartQuery(undefined, { skip: !isAuthenticated });
  const [updateCart, { isLoading: isLoadingUpdateCart }] =
    useUpdateCartMutation();

  const [getDocuments, { isLoading: isLoadingGetDocuments }] =
    useLazyGetDocumentsQuery();

  // ==========================================================================
  // Form
  // ==========================================================================

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleLoginClick = () => {
    openModal({
      modalId: 'loginRegister',
      children: (
        <LoginForm
          onLoginSuccess={() => closeModal('loginRegister')}
          useExternalLinks
        />
      ),
      size: '60rem',
    });
  };

  const handleGetFiles = async () => {
    try {
      const documents = await getDocuments({ id: product.code }).unwrap();
      setDocuments(documents);
    } catch (e) {
      if (isApiError(e)) {
        showNotification({
          title: t('products.error'),
          message: e.data.message,
          color: 'red',
        });
      } else {
        showNotification({
          title: t('products.unexpectedError'),
          message: t('products.errorOccurred'),
          color: 'red',
        });
      }
    }
  };

  const handleAddToCart = async () => {
    try {
      await updateCart({
        products: [
          ...(cart?.products ?? [])
            .filter((p) => p.productId !== product.code)
            .map((p) => ({
              productId: p.productId.toString(),
              productName: p.productName,
              unitPrice: p.unitPrice,
              quantity: p.quantity,
            })),
          {
            productId: product.code,
            productName: product.name,
            unitPrice: product.pricePromo ?? product.price!,
            quantity: selectedQuantity,
          },
        ],
      }).unwrap();
      setShowAddedToCartMessage(true);
    } catch (e) {
      if (isApiError(e)) {
        showNotification({
          title: t('products.error'),
          message: e.data.message,
          color: 'red',
        });
      } else {
        showNotification({
          title: t('products.unexpectedError'),
          message: t('products.errorOccurred'),
          color: 'red',
        });
      }
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Grid className={classes.grid} gutter={0}>
      <Grid.Col
        span={{ base: 12, md: 9 }}
        style={{
          borderRight: '1px solid #ccc',
          paddingRight: '3rem',
        }}
        className={classes.gridLeftContainer}
        p="xs"
      >
        <Group gap="xs" align="baseline">
          <Text fw="600" span c="brand" size="1.7rem" lh="xs">
            {product.name}
          </Text>

          <Text c="brand" size="lg" lh="xs" td="underline">
            {t('products.productCode')}:
            <Text span c="brand" fs="italic" size="xl" lh="xs">
              {product.code}
            </Text>
          </Text>
        </Group>
        {product.supplier && (
          <Text size="1.5rem" mt="sm">
            {product.supplier}
          </Text>
        )}

        {/* <Anchor
          target="_blank"
          td="underline"
          display="block"
          size="xs"
          mt="sm"
        >
          VISUALIZZA LA SCHEDA TECNICA
        </Anchor> */}

        {category === 'Cosmetico' && (
          <Text fw="600" mb="xs" mt="sm">
            {t('products.ingredients')}:{' '}
            <Text span fs="italic">
              {product.INCIName}
            </Text>
          </Text>
        )}

        {product.description && (
          <Text lh="xs" mt="sm">
            {product.description}
          </Text>
        )}
        {(product.isotype || product.hostSpecies || product.storageBuffer) && (
          <Group mt="lg" gap="xl">
            {product.isotype && (
              <div>
                <Text fw="600">{t('products.isotype')}:</Text>
                <Text>{product.isotype}</Text>
              </div>
            )}
            {product.hostSpecies && (
              <div>
                <Text fw="600">{t('products.hostSpecies')}:</Text>
                <Text>{product.hostSpecies}</Text>
              </div>
            )}

            {product.storageBuffer && (
              <div>
                <Text fw="600">{t('products.storageBuffer')}:</Text>
                <Text>{product.storageBuffer}</Text>
              </div>
            )}
          </Group>
        )}

        {(product.preservative || product.name) && (
          <Group gap="xs" mt="sm">
            {product.preservative && (
              <Text fw="600">
                {t('products.preservative')}:{' '}
                <Text span> {product.preservative}</Text>
              </Text>
            )}
            {product.preservative && (
              <Text fw="600">
                {t('products.constituents')}: <Text span>-</Text>
              </Text>
            )}
          </Group>
        )}

        <Group mt="sm" gap="xl">
          {product.form && (
            <div>
              <Text fw="600">{t('products.form')}:</Text>
              <Text>{product.form}</Text>
            </div>
          )}
          {product.clonality && (
            <div>
              <Text fw="600">{t('products.clonality')}:</Text>
              <Text>{product.clonality}</Text>
            </div>
          )}
        </Group>
      </Grid.Col>

      {category === 'Ricerca' && (
        <Grid.Col
          span={{ base: 12, md: 3 }}
          py="xs"
          className={classes.gridRightContainer}
        >
          {isAuthenticated ? (
            <>
              {(product.promoCode || product.promoEndDate) && (
                <Group mb="xs" gap="xs">
                  {product.promoCode && (
                    <Text size="sm" fw="bold">
                      {t('products.promoCode')}{' '}
                      <Text span>{product.promoCode}</Text>
                    </Text>
                  )}
                  {product.promoEndDate && (
                    <Text size="sm" fw="bold">
                      {t('products.promoEndDate')}{' '}
                      <Text span>{product.promoEndDate}</Text>
                    </Text>
                  )}
                </Group>
              )}

              {product.pricePromo != null && product.pricePromo > 0 ? (
                <>
                  <Group gap="xs" mt="lg" align="center">
                    <Text size="1.7rem" fw="600">
                      € {product.pricePromo}
                    </Text>
                    {product.price != null && product.price > 0 && (
                      <Text
                        size="1.2rem"
                        fw="400"
                        c="dimmed"
                        style={{ textDecoration: 'line-through' }}
                      >
                        € {product.price}
                      </Text>
                    )}
                  </Group>
                  <Text size="xs" c="dimmed">
                    {t('products.taxesExcluded')}
                  </Text>
                </>
              ) : product.price != null && product.price > 0 ? (
                <>
                  <Text size="1.7rem" fw="600">
                    € {product.price}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {t('products.taxesExcluded')}
                  </Text>
                </>
              ) : (
                <Text c="dimmed">{t('products.priceNotAvailable')} </Text>
              )}

              <NumberInput
                mt="lg"
                w="100%"
                label={t('products.quantity')}
                min={1}
                value={selectedQuantity}
                onChange={(value) => setSelectedQuantity(+value)}
              />
              <Button
                mt="md"
                fullWidth
                onClick={handleAddToCart}
                loading={isLoadingUpdateCart}
              >
                {t('products.addToCart')}
              </Button>
              {showAddedToCartMessage && (
                <Alert w="100%" mt="sm" icon={<IconShoppingCart />}>
                  <Text>{t('products.addedToCart')} </Text>
                  <Space h="0.5rem" />
                  <Anchor
                    size="sm"
                    href={t('cart.url')}
                    target="_blank"
                    rel="noopener"
                  >
                    {t('products.cart')}
                  </Anchor>
                </Alert>
              )}
            </>
          ) : (
            <>
              <Text mb="xs">{t('products.loginRequired')}</Text>
              <Button onClick={handleLoginClick}>{t('products.login')}</Button>
            </>
          )}
        </Grid.Col>
      )}

      {category === 'Cosmetico' && (
        <Grid.Col
          span={{ base: 12, md: 3 }}
          py="xs"
          className={classes.gridRightContainer}
        >
          {!isAuthenticated && (
            <>
              <Text mb="xs">{t('products.loginRequiredDocuments')}</Text>
              <Button onClick={handleLoginClick}>{t('products.login')}</Button>
            </>
          )}
        </Grid.Col>
      )}

      {(product.file2 || product.file3) &&
        category === 'Ricerca' &&
        isAuthenticated && (
          <>
            <Grid.Col
              span={12}
              style={{ borderBottom: '1px solid #ccc' }}
              p="xs"
            >
              <Group>
                <Text fw="600" size="lg">
                  {t('products.documents')}
                </Text>
              </Group>{' '}
            </Grid.Col>
            <Grid.Col my="xs" span={12}>
              {product.file2 && (
                <Text>
                  <Anchor
                    href={normalizeUrl(product.file2)}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                  >
                    {`File ${product.file2.split('/').pop()}`}
                  </Anchor>
                </Text>
              )}

              {product.file3 && (
                <Text>
                  <Anchor
                    href={normalizeUrl(product.file3)}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                  >
                    {product.file3.split('/').pop()}
                  </Anchor>
                </Text>
              )}
            </Grid.Col>
          </>
        )}
      {category !== 'Ricerca' && isAuthenticated && (
        <>
          <Grid.Col span={12} style={{ borderBottom: '1px solid #ccc' }} p="xs">
            <Group>
              <Text fw="600" size="lg">
                {t('products.documents')}
              </Text>
            </Group>
          </Grid.Col>

          <Grid.Col my="xs" span={12}>
            {isLoadingGetDocuments && (
              <Text size="sm">{t('products.searching')}</Text>
            )}

            {documents &&
              documents.map((document: GetDocuementsResponse) => (
                <Group key={document.id}>
                  <Text size="sm">{document.name}</Text>
                  <Anchor
                    href={`https://api.prodottigianni.com/products/globe-documents/${document.id}`}
                    download
                  >
                    <ActionIcon variant="light">
                      <IconDownload size={17} />
                    </ActionIcon>
                  </Anchor>
                </Group>
              ))}

            {documents && documents.length === 0 && !isLoadingGetDocuments && (
              <Text size="sm" c="dimmed" fs="italic">
                {t('products.noDocumentsFound')}
              </Text>
            )}

            {!documents && !isLoadingGetDocuments && (
              <Button
                variant="subtle"
                leftSection={<IconEye size={16} />}
                onClick={handleGetFiles}
              >
                {t('products.viewDocuments')}
              </Button>
            )}
          </Grid.Col>
        </>
      )}
      {product.file1 && isAuthenticated && (
        <>
          <Grid.Col span={12} style={{ borderBottom: '1px solid #ccc' }} p="xs">
            <Text fw="600" size="lg">
              {t('products.images')}
            </Text>
          </Grid.Col>
          <Grid.Col span={12}>
            <Group mt="xs">
              <Anchor
                href={normalizeUrl(product.file1)}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
              >
                <img
                  src={product.file1}
                  alt={product.name + '_1'}
                  style={{ width: '30%' }}
                />
              </Anchor>
            </Group>
          </Grid.Col>
        </>
      )}
      {/*// TODO domandare*/}
      {/* <Grid.Col span={12} p="xs">
        <Group>
          <Button variant="default">PUBBLICATIONS</Button>
          <Button variant="default">REQUEST INFO</Button>
        </Group>
      </Grid.Col> */}
    </Grid>
  );
}
